/**
 * EcosystemGraphPreview Component
 * A non-interactive miniature force-directed graph displayed inside a circular
 * clip mask. Gives visitors a visual taste of the ecosystem network before
 * they navigate to the full Directory graph.
 *
 * Graph structure:
 * - Nodes: Organizations from GET /api/graph/full, filtered to only those
 *   that have at least one connection (via filterConnectedNodes). Removing
 *   isolated nodes keeps the preview visually dense and interesting.
 * - Links: Organization-to-organization relationships, drawn as thin
 *   (#E0E0E0, 0.5px) lines to stay subtle at this small scale.
 *
 * Force simulation:
 * - Uses react-force-graph-2d defaults with no custom force overrides.
 * - cooldownTicks=50 (lower than other graphs) so the simulation settles
 *   quickly; the preview is decorative, not exploratory.
 * - onEngineStop calls zoomToFit(200, 20) to fill the circular viewport
 *   with 20px padding.
 *
 * Node rendering:
 * - Simplified canvas drawing: filled circles only, no labels, no hover
 *   rings. Nodes use the uniform 6 px size from getNodeSize() and are
 *   colored by sector.
 *
 * Non-interactive mode:
 * - enableNodeDrag, enableZoomInteraction, and enablePanInteraction are
 *   all set to false. The graph is view-only.
 *
 * Circular clip mask:
 * - The ForceGraph2D canvas is placed inside a Card with the Bootstrap
 *   class "rounded-circle overflow-hidden", which clips it to a circle.
 *   Width and height are both set to the `height` prop so the card is
 *   a perfect square (and thus a perfect circle when clipped).
 *
 * Key interactions:
 * - Click anywhere on the preview to navigate to /directory (the full
 *   ecosystem graph). The entire container is a clickable button with
 *   keyboard support (Enter key).
 *
 * A "?" explainer (GraphExplainer, #27) sits in the top-right corner
 * explaining what the visualization shows. It is rendered as a SIBLING of
 * the `role="button"` container, not nested inside it: interactive content
 * nested inside an element with `role="button"` is invalid per ARIA and is
 * unreliably exposed by assistive tech (several screen readers, notably
 * VoiceOver, flatten the parent into a single button and never surface an
 * inner control) — precisely the audience this ticket is written for. The
 * two are visually stacked via a shared position-relative wrapper sized to
 * the circle instead.
 */

import React, {
    useRef,
    useCallback,
    useEffect,
    useState,
    useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import ForceGraph2D from 'react-force-graph-2d';
import { Card, Spinner } from 'react-bootstrap';

import { useEcosystem } from '@/shared/contexts/EcosystemContext';

import { useGraphData } from '../hooks/useGraphData';
import { getNodeColor } from '../utils/graphColors';
import { filterConnectedNodes, getNodeSize } from '../utils/graphHelpers';

import GraphExplainer from './GraphExplainer';

import '../Graph.css';

export default function EcosystemGraphPreview({ height }) {
    const navigate = useNavigate();
    const { ecosystem } = useEcosystem();
    const ecosystemName = ecosystem?.name || 'ecosystem';
    const graphRef = useRef();
    const containerRef = useRef();
    const [, setDimensions] = useState({ width: 400, height });

    // Fetch full graph data (no filters for preview)
    const { graphData, loading, error } = useGraphData();

    // --- Data transformation ---
    // Strip out isolated (unconnected) nodes so the preview shows only
    // the connected network structure. filterConnectedNodes builds a set
    // of node IDs that appear in at least one link, then drops the rest.
    // This keeps the preview visually dense and meaningful.
    const displayedData = useMemo(() => {
        return filterConnectedNodes(graphData);
    }, [graphData]);

    // Handle responsive sizing
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                setDimensions({ width, height });
            }
        };

        updateDimensions();
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, [height]);

    // Navigate to directory on click
    const handleClick = useCallback(() => {
        navigate('/directory');
    }, [navigate]);

    // --- Custom node rendering (Canvas 2D) ---
    // Simplified renderer: filled circles only, no labels, no hover rings;
    // nodes use the uniform 6 px size from getNodeSize() and are colored by
    // sector.
    const nodeCanvasObject = useCallback((node, ctx) => {
        const color = getNodeColor(node, 'sector');
        const size = getNodeSize();

        ctx.beginPath();
        ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.fill();
    }, []);

    // Loading state
    if (loading) {
        // Height set by parent prop -- must be inline
        return (
            <Card
                className="border-0 bg-transparent text-center"
                style={{ height }}
            >
                <Card.Body className="d-flex align-items-center justify-content-center">
                    <Spinner animation="border" variant="light" size="sm" />
                </Card.Body>
            </Card>
        );
    }

    // Error state - show placeholder
    if (error || !displayedData.nodes.length) {
        // Height set by parent prop -- must be inline
        return (
            <Card
                className="border-0 bg-white bg-opacity-10 ecosystem-preview-placeholder"
                style={{ height }}
                onClick={handleClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleClick()}
            >
                <Card.Body className="d-flex flex-column align-items-center justify-content-center">
                    <div className="preview-empty-icon">🌐</div>
                    <small className="text-white-50 mt-2">
                        Explore the Ecosystem
                    </small>
                </Card.Body>
            </Card>
        );
    }

    return (
        <div ref={containerRef} className="ecosystem-graph-preview">
            {/* position-relative wrapper (not the circular Card itself, which
                is overflow-hidden and would clip the explainer trigger),
                sized to match the circle exactly so the explainer overlay
                anchors to the circle's corner rather than this wrapper's
                full-width box. Dimensions come from the `height` prop, same
                as the Card below -- must be inline. */}
            <div
                className="position-relative mx-auto"
                style={{ width: height, height: height }}
            >
                {/* role="button" lives on this div, not the outer container,
                    so GraphExplainer's trigger below is a SIBLING rather than
                    nested inside it — nesting an interactive control inside
                    role="button" is invalid ARIA and several screen readers
                    (VoiceOver in particular) never expose the inner control
                    at all. Being a sibling also means the trigger's click
                    never bubbles into this handler, so no stopPropagation is
                    needed to keep opening the explainer from also
                    navigating away from it. */}
                <div
                    className="h-100"
                    onClick={handleClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleClick()}
                    aria-label={`Interactive preview of the ${ecosystemName} network. Click to explore the full graph.`}
                >
                    {/* Circular dimensions from height prop -- must be inline */}
                    <Card
                        className="border-0 bg-white rounded-circle overflow-hidden shadow mx-auto"
                        style={{
                            width: height,
                            height: height,
                        }}
                    >
                        {/* ForceGraph2D configured as a non-interactive preview.
                            - width and height are both set to the `height` prop so
                              the canvas is square, matching the rounded-circle clip.
                            - linkWidth=0.5: thinner links than the full graph (1px)
                              to stay subtle at this small scale.
                            - cooldownTicks=50: fewer simulation iterations than the
                              full graph (100) since the preview is decorative and
                              speed matters more than perfect layout.
                            - All interaction disabled: no drag, no zoom, no pan.
                              The entire preview is a single click target. */}
                        <ForceGraph2D
                            ref={graphRef}
                            graphData={displayedData}
                            width={height}
                            height={height}
                            nodeCanvasObject={nodeCanvasObject}
                            nodePointerAreaPaint={(node, color, ctx) => {
                                const size = getNodeSize();
                                ctx.fillStyle = color;
                                ctx.beginPath();
                                ctx.arc(
                                    node.x,
                                    node.y,
                                    size + 2,
                                    0,
                                    2 * Math.PI
                                );
                                ctx.fill();
                            }}
                            linkColor={() => '#E0E0E0'}
                            linkWidth={0.5}
                            cooldownTicks={50}
                            onEngineStop={() => {
                                if (graphRef.current) {
                                    graphRef.current.zoomToFit(200, 20);
                                }
                            }}
                            enableNodeDrag={false}
                            enableZoomInteraction={false}
                            enablePanInteraction={false}
                        />
                    </Card>
                </div>

                {/* "How to read this map" explainer (#27) — a sibling of the
                    role="button" div above, not nested inside it. */}
                <div className="graph-preview-explainer-overlay">
                    <GraphExplainer variant="outline-light" />
                </div>
            </div>
            <div className="text-center mt-2">
                <small className="text-white-50">
                    Click to explore the ecosystem
                </small>
            </div>
        </div>
    );
}

EcosystemGraphPreview.propTypes = {
    height: PropTypes.number,
};

EcosystemGraphPreview.defaultProps = {
    height: 300,
};
